import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "react-bootstrap/Spinner";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Badge from "react-bootstrap/Badge";
import Alert from "react-bootstrap/Alert";
import {
  selectAllIgniteProducts,
  getIgniteProducts,
  igniteProductsTotalCount,
  igniteProductsStatus
} from '../../reducers/catalog';
import Product from "./Product";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import MSMPagination from '../MSMPagination';
import { Container, Row, Col, Button } from "react-bootstrap";
import { Search, XCircle } from 'react-bootstrap-icons';
import CategoryService from "../../services/category.service";

const apiUrl = process.env.REACT_APP_LOCALHOST_URL;
const PER_PAGE = 24;

const AdminIgniteProducts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const catalog = useSelector(selectAllIgniteProducts);
  const totalCount = useSelector(igniteProductsTotalCount);
  const listStatus = useSelector(igniteProductsStatus);

  const categoryId = searchParams.get('categoryId') || 'all';
  const productType = searchParams.get('productType') || 'all';
  const searchTerm = searchParams.get('search') || '';

  const [searchInput, setSearchInput] = useState(searchTerm);
  const [categories, setCategories] = useState([]);
  const [resetPagination, setResetPagination] = useState(false);
  const [range, setRange] = useState([1, PER_PAGE]);
  const [flash, setFlash] = useState(location.state?.flash || null);

  useEffect(() => {
    CategoryService.getIgniteCategories()
      .then(data => setCategories(Array.isArray(data) ? data : []))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    setSearchInput(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    dispatch(getIgniteProducts({
      page: range[0],
      perPage: range[1],
      search: searchTerm,
      categoryId,
      productType
    }));
  }, [dispatch, range, searchTerm, categoryId, productType]);

  const reloadData = (nextRange) => {
    setRange(nextRange);
  };

  const updateFilters = (updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value == null || value === '' || value === 'all') {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });
    setSearchParams(next);
    setRange([1, PER_PAGE]);
    setResetPagination(prev => !prev);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    updateFilters({ search: searchInput.trim() });
  };

  const handleClearSearch = () => {
    setSearchInput('');
    updateFilters({ search: '' });
  };

  const categoryLabel = (cat) => cat.overrideCategoryName || cat.categoryName;

  return (
    <div className="container">
      <h1>Ignite Products</h1>
      <p className="text-muted mb-3">
        Browse Ignite styles by category, type, or search. {totalCount} match current filters.
      </p>

      {flash && (
        <Alert variant={flash.variant} dismissible onClose={() => setFlash(null)}>
          {flash.message}
        </Alert>
      )}

      <div className="mb-3 d-flex flex-wrap gap-2">
        <Link to="/admin/addproduct">
          <Button variant="success">Add Product</Button>
        </Link>
        <Link to="/admin/ignite/inventory">
          <Button variant="primary">Manage Ignite Inventory</Button>
        </Link>
      </div>

      <Container className="mb-3 px-0">
        <Row className="align-items-end g-3">
          <Col md={5}>
            <Form onSubmit={handleSearchSubmit}>
              <Form.Label>Search</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Title, model code, or maker"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                <Button variant="primary" type="submit">
                  <Search /> Search
                </Button>
                {searchTerm && (
                  <Button variant="outline-secondary" onClick={handleClearSearch}>
                    <XCircle /> Clear
                  </Button>
                )}
              </InputGroup>
            </Form>
          </Col>
          <Col md={4} className="text-muted">
            {searchTerm && (
              <Badge bg="secondary" className="me-2">
                Search: {searchTerm}
              </Badge>
            )}
            {productType !== 'all' && (
              <Badge bg="info" className="me-2">{productType}</Badge>
            )}
          </Col>
        </Row>
      </Container>

      <MSMPagination
        reload={reloadData}
        perPage={PER_PAGE}
        total={totalCount}
        reset={resetPagination}
      />

      {listStatus === 'loading' && <Spinner animation="border" className="my-3" />}
      {listStatus === 'failed' && <div className="text-danger">Error Loading Ignite Products</div>}

      <Container>
        <Row>
          {listStatus === 'succeeded' && catalog && catalog.map(catalogItem => (
            <Product
              key={catalogItem.styleId || catalogItem.id}
              product={catalogItem}
              baseUrl={apiUrl}
              url="../admin/allproduct/details/"
            />
          ))}
        </Row>
        {listStatus === 'succeeded' && (!catalog || catalog.length === 0) && (
          <div className="mt-3 text-muted">No Ignite products match these filters.</div>
        )}
      </Container>
    </div>
  );
};

export default AdminIgniteProducts;
